import { formatRangeVal } from "../betTableLucky7/lucky7Utils";
import { formatBook } from "../../../utils/formatOdds";

/**
 * Lucky 5 (lucky5) bet table in the reference admin's markup — rendered inside the page's `.casino-detail`
 * of `.casino-table.lucky7` (the reference reuses the Lucky 7 table classes):
 *   .casino-box.low-high-box (Low | 6 card | High + range)
 *   .casino-box.sidebets-box (.lucky7-extra-bets: Even, Odd, Black, Red — each + range)
 *   .casino-box.cards-box (11 single cards A..10, J — one v-for, no whitespace between items + range)
 * Styled by the ported reference rules in styles/index.css (+ styles/games/lucky7aaa.css).
 *
 * Markets are read like the reference (`mainData` = filter(sub, {subtype: "lucky5"}), API order):
 *   0 Low, 1 High, 2 Even, 3 Odd, 4 Red, 5 Black, 6-16 Card 1..10, J — with a lookup by `nat` first.
 */
const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT = `${import.meta.env.BASE_URL}assets/img/icons`;
// Reference: n = 1..11 → mainData[n + 5]; image A.png for 1, J.png for 11, else n.png
const CARD_IMAGES = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J"];
const NAMES = [
  "Low Card",
  "High Card",
  "Even",
  "Odd",
  "Red",
  "Black",
  ...CARD_IMAGES.map((v) => `Card ${v === "A" ? "1" : v}`),
];

const isSuspended = (b) => String(b?.gstatus || "").toUpperCase() !== "OPEN";
const odds = (b) => (b?.b !== undefined && b?.b !== null && b?.b !== "" ? b.b : 0);

function lucky5Markets(sub = []) {
  const all = Array.isArray(sub) ? sub : [];
  const own = all.filter((s) => s?.subtype === "lucky5");
  const list = own.length ? own : all;
  const byNat = (name) => list.find((s) => String(s?.nat || "").toLowerCase() === name.toLowerCase());
  return NAMES.map((name, i) => byNat(name) || (name === "Card 1" && byNat("Card A")) || list[i] || { nat: name });
}

export default function BetTableLucky5({ sub = [], onBetClick, exposures = {} }) {
  const m = lucky5Markets(sub);
  const getExposure = (name) => {
    const v = exposures[name] ?? exposures[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  // Reference v-casinofancybook: amount > 0 → book-green, otherwise book-red
  const book = (b) => {
    const exp = getExposure(b?.nat);
    return <div className={`casino-book ${exp > 0 ? "book-green" : "book-red"}`}>{formatBook(exp)}</div>;
  };
  const click = (b) => {
    if (isSuspended(b)) return;
    onBetClick?.(odds(b), b?.nat, null, b);
  };
  const susp = (b) => (isSuspended(b) ? " suspended" : "");
  // Reference book directive adds .lock-top to a suspended parent of the book element
  const suspLock = (b) => (isSuspended(b) ? " suspended lock-top" : "");

  const range = (b) => (
    <div className="casino-min-max text-right">
      {"\n                                R:"}
      <span>{formatRangeVal(b?.min)}</span>-<span>{formatRangeVal(b?.max)}</span>
    </div>
  );

  const lowHigh = (cls, b) => (
    <div className={`${cls}${suspLock(b)}`} onClick={() => click(b)}>
      <div className="casino-odds">{odds(b)}</div>{" "}
      <div className="text-center casino-buttons">
        <span>{b?.nat}</span>
      </div>{" "}
      {book(b)}
    </div>
  );

  const extraBet = (b, content) => (
    <div className="lucky7-extra-bets-item-container">
      <div className={`lucky7-extra-bets-item${suspLock(b)}`} onClick={() => click(b)}>
        <div className="casino-odds">{odds(b)}</div> <div className="casino-buttons">{content}</div> {book(b)}
      </div>{" "}
      {range(b)}
    </div>
  );

  return (
    <>
      <div className="casino-box low-high-box">
        {lowHigh("low-odds", m[0])}{" "}
        <div className="text-center lucky7-card">
          <img src={`${IMG}/6.png`} className="img-fluid" alt="" />
        </div>{" "}
        {lowHigh("high-odds", m[1])}{" "}
        {range(m[0])}
      </div>{" "}
      <div className="casino-box sidebets-box">
        <div className="lucky7-extra-bets">
          {extraBet(m[2], m[2]?.nat || "Even")}{" "}
          {extraBet(m[3], m[3]?.nat || "Odd")}{" "}
          {extraBet(
            m[5],
            <>
              <img src={`${SUIT}/spade.png`} alt="" /> <img src={`${SUIT}/club.png`} alt="" />
            </>
          )}{" "}
          {extraBet(
            m[4],
            <>
              <img src={`${SUIT}/heart.png`} alt="" /> <img src={`${SUIT}/diamond.png`} alt="" />
            </>
          )}
        </div>
      </div>{" "}
      <div className="casino-box cards-box">
        <div className="w-100">
          <div className="casino-odds">{`\n            ${odds(m[6])}\n          `}</div>{" "}
          <div className="casino-cards text-center mt-1">
            {CARD_IMAGES.map((img, i) => {
              const b = m[6 + i];
              return (
                <div key={img} className="casino-card-item">
                  <div className={`card-image${susp(b)}`} onClick={() => click(b)}>
                    <img src={`${IMG}/${img}.png`} alt="" />
                  </div>{" "}
                  {book(b)}
                </div>
              );
            })}
          </div>
        </div>{" "}
        {range(m[6])}
      </div>
    </>
  );
}
